import styled, { css } from 'styled-components';

import { breakpoints } from 'theme';

/** Aligned with `ChangePlan/styled.ts` — subscription & billing UI consistency */
const g = '#0f241f';
const gm = '#00b8a8';
const gl = '#e1f5ee';
const gp = '#f0faf6';
const t = '#111';
const t2 = '#555';
const t3 = '#999';
const bd = '#e2e8e4';
const sf = '#f8faf9';
const am = '#f5a623';
const amp = '#fff8ed';

const headline = css`
  font-weight: 600;
  color: ${t};
`;

export const Panel = styled.div`
  width: min(560px, calc(100vw - 32px));
  max-height: min(82vh, 680px);
  display: flex;
  flex-direction: column;
  background: #fff;
  border-radius: 20px;
  border: 1.5px solid ${bd};
  box-shadow: 0 8px 40px rgba(15, 36, 31, 0.12);
  overflow: hidden;
`;

export const PanelHeader = styled.div`
  padding: 22px 24px 18px;
  flex-shrink: 0;
  background: linear-gradient(180deg, ${gp} 0%, #fff 48%);
  border-bottom: 1px solid ${bd};
`;

export const HeaderTop = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 10px;
`;

export const Eyebrow = styled.span`
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: ${t3};
`;

export const SlotPill = styled.span<{ $complete: boolean }>`
  flex-shrink: 0;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  padding: 5px 12px;
  border-radius: 40px;
  border: 1.5px solid ${({ $complete }) => ($complete ? gm : bd)};
  background: ${({ $complete }) => ($complete ? '#fff' : sf)};
  color: ${({ $complete }) => ($complete ? g : t3)};
  transition: border-color 0.15s ease, background 0.15s ease, color 0.15s ease;
`;

export const TitleRow = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 12px;
`;

export const TitleIcon = styled.span`
  flex-shrink: 0;
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 12px;
  background: #fff;
  border: 1.5px solid ${gl};
  font-size: 18px;
  line-height: 1;
`;

export const TitleBlock = styled.div`
  flex: 1;
  min-width: 0;
`;

export const Title = styled.h2`
  ${headline}
  margin: 0;
  font-size: 21px;
  line-height: 1.25;
  letter-spacing: -0.02em;
`;

export const Intro = styled.p`
  margin: 8px 0 0;
  font-size: 14px;
  line-height: 1.55;
  color: ${t2};
`;

export const ProgressBlock = styled.div`
  margin-top: 18px;
`;

export const SelectionHint = styled.p<{ $ready?: boolean }>`
  margin: 10px 0 0;
  font-size: 12px;
  font-weight: 600;
  line-height: 1.4;
  color: ${({ $ready }) => ($ready ? gm : t2)};
`;

export const BarTrack = styled.div`
  width: 100%;
  height: 6px;
  background: ${gl};
  border-radius: 4px;
  overflow: hidden;
`;

export const BarFill = styled.div<{ $pct: number }>`
  height: 100%;
  border-radius: 4px;
  width: ${({ $pct }) => $pct}%;
  background: ${gm};
  transition: width 0.35s ease;
`;

export const OnceNote = styled.div`
  margin-top: 16px;
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 12px 14px;
  font-size: 12px;
  line-height: 1.5;
  color: #7a5c00;
  background: ${amp};
  border-radius: 12px;
  border-left: 4px solid ${am};
`;

export const OnceNoteIcon = styled.span`
  flex-shrink: 0;
  font-size: 14px;
  line-height: 1.2;
  margin-top: 1px;
`;

export const OnceNoteText = styled.div`
  flex: 1;
  min-width: 0;
`;

export const ListSection = styled.div`
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  background: ${sf};
`;

export const ListHeading = styled.div`
  padding: 10px 20px 4px;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: ${t3};
  flex-shrink: 0;
`;

export const List = styled.ul`
  list-style: none;
  margin: 0;
  padding: 4px 14px 14px;
  overflow-y: auto;
  flex: 1;
  min-height: 0;

  &::-webkit-scrollbar {
    width: 6px;
  }
  &::-webkit-scrollbar-thumb {
    background: ${bd};
    border-radius: 6px;
  }
`;

export const EmptyMessage = styled.div`
  margin: 0;
  padding: 36px 28px;
  font-size: 14px;
  line-height: 1.55;
  color: ${t2};
  text-align: center;
  background: ${sf};
`;

export const EmptyIcon = styled.div`
  font-size: 32px;
  line-height: 1;
  margin-bottom: 12px;
`;

export const Row = styled.li<{ $selected: boolean }>`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 10px;
  border-radius: 12px;
  cursor: pointer;
  user-select: none;
  border: ${({ $selected }) =>
    $selected ? `2px solid ${gm}` : `1.5px solid ${bd}`};
  background: ${({ $selected }) => ($selected ? '#fff' : '#fff')};
  margin-bottom: 6px;
  box-shadow: ${({ $selected }) =>
    $selected
      ? '0 2px 10px rgba(0, 184, 168, 0.1)'
      : '0 1px 2px rgba(15, 36, 31, 0.05)'};
  transition: border-color 0.18s ease, box-shadow 0.18s ease,
    transform 0.18s ease;

  &:last-child {
    margin-bottom: 0;
  }

  &:hover {
    border-color: ${({ $selected }) => ($selected ? gm : g)};
    box-shadow: 0 4px 14px rgba(15, 36, 31, 0.07);
    transform: translateY(-1px);
  }

  &:focus-visible {
    outline: 2px solid ${gm};
    outline-offset: 2px;
  }
`;

/** Circular avatar frame — image & initials are clipped to a circle */
export const AvatarWrap = styled.div<{ $selected: boolean }>`
  width: 36px;
  height: 36px;
  min-width: 36px;
  border-radius: 50%;
  flex-shrink: 0;
  overflow: hidden;
  background: ${sf};
  border: 1.5px solid ${({ $selected }) => ($selected ? gm : bd)};
  box-shadow: ${({ $selected }) => ($selected ? `0 0 0 2px ${gp}` : 'none')};
  transition: border-color 0.18s ease, box-shadow 0.18s ease;
`;

export const Avatar = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
  border-radius: 50%;
`;

export const InitialsAvatar = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.02em;
  line-height: 1;
  color: ${g};
  background: linear-gradient(145deg, ${gl} 0%, #fff 100%);
  border-radius: 50%;
`;

export const RowText = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  align-items: center;
  gap: 8px;
`;

export const RowName = styled.div`
  flex: 1;
  min-width: 0;
  font-size: 14px;
  font-weight: 600;
  line-height: 1.25;
  color: ${t};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

export const RowMetaRow = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  flex-shrink: 0;
  flex-wrap: wrap;
`;

export const StatusPill = styled.span<{ $active: boolean }>`
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  padding: 2px 7px;
  border-radius: 40px;
  ${({ $active }) =>
    $active
      ? css`
          background: ${gp};
          color: ${g};
          border: 1px solid ${gl};
        `
      : css`
          background: #f3f4f6;
          color: ${t3};
          border: 1px solid ${bd};
        `}
`;

export const SelectionMark = styled.span<{ $selected: boolean }>`
  flex-shrink: 0;
  width: 22px;
  height: 22px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 11px;
  font-weight: 700;
  line-height: 1;
  color: ${({ $selected }) => ($selected ? '#fff' : t3)};
  background: ${({ $selected }) => ($selected ? gm : '#fff')};
  border: 1.5px solid ${({ $selected }) => ($selected ? gm : bd)};
  transition: background 0.18s ease, border-color 0.18s ease, color 0.18s ease;
`;

export const Footer = styled.div`
  display: flex;
  gap: 10px;
  padding: 16px 20px 20px;
  border-top: 1px solid ${bd};
  justify-content: flex-end;
  flex-wrap: wrap;
  background: #fff;

  @media ${breakpoints.downSmPlus} {
    flex-direction: column;
    align-items: stretch;
  }
`;

export const BtnGhost = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 12px 22px;
  border-radius: 40px;
  font-size: 14px;
  font-weight: 600;
  font-family: inherit;
  cursor: pointer;
  transition: all 0.15s ease;
  background: transparent;
  border: 1.5px solid ${bd};
  color: ${t2};

  &:hover:not(:disabled) {
    border-color: ${g};
    color: ${g};
  }

  &:disabled {
    opacity: 0.45;
    cursor: not-allowed;
  }

  @media ${breakpoints.downSmPlus} {
    width: 100%;
  }
`;

export const BtnPrimary = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 12px 26px;
  border-radius: 40px;
  font-size: 14px;
  font-weight: 600;
  font-family: inherit;
  cursor: pointer;
  transition: background 0.15s ease;
  background: ${g};
  border: none;
  color: #fff;
  min-width: 148px;

  &:hover:not(:disabled) {
    background: #0a1a14;
  }

  &:disabled {
    opacity: 0.45;
    cursor: not-allowed;
  }

  @media ${breakpoints.downSmPlus} {
    width: 100%;
    min-width: 0;
  }
`;
